import { customElement, html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { TemplateResult } from '@inter-ikea-kompis/web-component';

import DragAndDropItemEventEnum from '../../enums/DragAndDropItemEventEnum';
import DragDirectionEnum from '../../enums/DragDirectionEnum';
import type IPointerPosition from '../../types/IPointerPosition';
import DragAndDropItemStyle from './DragAndDropItemStyle';

/**
 * Drag and Drop Item component.
 *
 * @event dragItem Emits when dragging an item
 * @event invalidDrag Emits when an item couldn't be dragged to a position
 * @event ghostPositionUpdate Emits the updated position of the dragged item
 */
@customElement('kompis-drag-and-drop-item')
export default class DragAndDropItem extends KompisElement {
	// An object data to be transfered.
	@property({ type: Object })
	public data = null;

	// Set dragged state.
	@property({ type: Boolean })
	public isDragged = false;

	// Start drag on direction.
	@property({ type: String })
	public dragDirection = DragDirectionEnum.horizontal;

	// Start drag on angle.
	@property({ type: Number })
	public dragAngleLimit = 50;

	// Start drag on offset.
	@property({ type: Number })
	public dragOffset = 40;

	/* eslint-disable @typescript-eslint/no-explicit-any */
	public update: any;
	/* eslint-enable @typescript-eslint/no-explicit-any */

	private startPosition: IPointerPosition | null = null;
	private listeners: { [key: string]: () => void } = {
		pointerUp: this.pointerUp.bind(this),
		pointerMove: this.pointerMove.bind(this),
		onTouchMove: this.onTouchMove.bind(this)
	};

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public render(): TemplateResult {
		return html`
			<style>
				${DragAndDropItemStyle.getStyle()}
			</style>
			<div
				class="item-container"
				@touchstart="${{
					passive: false,
					handleEvent: (event: TouchEvent): void => this.onTouchDown(event)
				}}"
				@mousedown="${{
					passive: false,
					handleEvent: (event: MouseEvent): void => this.pointerDown(event)
				}}"
			>
				<div>
					<slot></slot>
				</div>
			</div>
		`;
	}

	/**
	 * Triggered when the element is connected.
	 */
	public connectedCallback(): void {
		super.connectedCallback();
		// Pointer up events
		document.body.addEventListener('mouseup', this.listeners.pointerUp);
		document.body.addEventListener('touchend', this.listeners.pointerUp);
		// Move events
		document.body.addEventListener('mousemove', this.listeners.pointerMove);
		document.body.addEventListener('touchmove', this.listeners.onTouchMove);
	}

	/**
	 * Triggered when the component is removed from DOM.
	 */
	public disconnectedCallback(): void {
		super.disconnectedCallback();
		// Pointer up events
		document.body.removeEventListener('mouseup', this.listeners.pointerUp);
		document.body.removeEventListener('touchend', this.listeners.pointerUp);
		// Move events
		document.body.removeEventListener('mousemove', this.listeners.pointerMove);
		document.body.removeEventListener('touchmove', this.listeners.onTouchMove);
	}

	/**
	 * Handles pointer down event.
	 *
	 * @param event MouseEvent.
	 */
	private pointerDown(event: MouseEvent): void {
		event.preventDefault();
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.clientX;
		position.y = event.clientY;
		this.startPosition = position;
		this.update();
	}

	/**
	 * Handles touch down event.
	 *
	 * @param event TouchEvent.
	 */
	private onTouchDown(event: TouchEvent): void {
		event.preventDefault();
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.touches[0].clientX;
		position.y = event.touches[0].clientY;
		this.startPosition = position;
		this.update();
	}

	/**
	 * Handles pointer move event and emits event if item was dragged.
	 *
	 * @param event MouseEvent.
	 */
	private pointerMove(event: MouseEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.clientX;
		position.y = event.clientY;
		this.drag(position);
		if (this.isDragged) {
			this.update();
			return;
		}
	}

	/**
	 * Handles touch move event and enits event if item was dragged.
	 *
	 * @param event TouchEvent.
	 */
	private onTouchMove(event: TouchEvent): void {
		const position: IPointerPosition = { x: null, y: null };
		position.x = event.touches[0].clientX;
		position.y = event.touches[0].clientY;
		this.drag(position);
		if (this.isDragged) {
			this.update();
			return;
		}
	}

	/**
	 * Checks if the item can be dragged and emits an event.
	 *
	 * @param position PointerPosition.
	 */
	private drag(position: IPointerPosition): void {
		if (this.isDragged) {
			this.dispatchEvent(
				new CustomEvent(DragAndDropItemEventEnum.ghostPositionUpdate, {
					bubbles: true,
					detail: { position: position }
				})
			);
		}

		if (position.x && position.y && this.startPosition?.x && this.startPosition?.y) {
			const moveOffset = Math.hypot(
				Math.abs(position.x - this.startPosition.x),
				Math.abs(position.y - this.startPosition.y)
			);
			if (moveOffset >= this.dragOffset) {
				const angle: number = this.getAngle(this.startPosition, position);
				if (this.isDragAngle(angle)) {
					this.dispatchEvent(
						new CustomEvent(DragAndDropItemEventEnum.dragItem, {
							bubbles: true,
							detail: {
								data: this.data,
								position: position
							}
						})
					);
				} else {
					this.dispatchEvent(new Event(DragAndDropItemEventEnum.invalidDrag, { bubbles: true }));
					this.update();
				}
				this.startPosition = null;
			}
		}
	}

	/**
	 * Handles pointer up event.
	 */
	private pointerUp(): void {
		this.startPosition = null;
	}

	/**
	 * Calculates angle between 2 points.
	 *
	 * @param p1 PointerPosition.
	 * @param p2 PointerPosition.
	 * @returns Number.
	 *
	 */
	private getAngle(p1: IPointerPosition, p2: IPointerPosition): number {
		if (p1.x !== null && p1.y !== null && p2.x !== null && p2.y !== null) {
			return (Math.atan2(Math.abs(p2.y - p1.y), Math.abs(p2.x - p1.x)) * 180) / Math.PI;
		}
		throw new Error("Can't create angle without PointerPosition");
	}

	/**
	 * Returns true if angle is within drag angle limit.
	 *
	 * @param angle Number.
	 * @returns Boolean.
	 *
	 */
	private isDragAngle(angle: number): boolean {
		if (this.dragDirection == DragDirectionEnum.horizontal) {
			return angle >= this.dragAngleLimit;
		} else {
			return angle <= this.dragAngleLimit;
		}
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-drag-and-drop-item': DragAndDropItem;
	}
}
